import inflection from 'inflection'
import {capitalize, isEmpty} from 'lodash'
import {CorruptedDataError, UniqueRecordError} from '#/notes/utilities/errors'
import {defaultPagination} from '#/notes/components/definitions'

export const AdminDataTable = {
  inject: ['$validator'],
  props: {
    itemType: { type: String },
    addItem: { type: Function },
    fetchItems: { type: Function },
    updateItem: { type: Function, default: () => {} },
    formFields: { type: Array, default: () => [] }
  },
  data () {
    return {
      dataTableRows: [],
      formData: this.getDefaultFormValues(),
      isDialogOpen: false,
      isLoading: false,
      ordering: null,
      pagination: defaultPagination,
      searchQuery: ''
    }
  },
  computed: {
    itemsPerPage () {
      return this.pagination.pageSize
    },
    customPagination () {
      return {
        totalItemCount: this.pagination.total,
        currentPage: this.pagination.currentPage
      }
    }
  },
  methods: {
    getDefaultFormValues () {
      return this.formFields.reduce((o, k) => { o[k] = ''; return o }, {})
    },
    determineActionTerm (isActive) {
      return isActive ? 'activate' : 'deactivate'
    },
    getActionableRows (rows, isActive) {
      return rows.filter(row => row.active !== isActive)
    },
    async updateItems (items, isActive) {
      return await Promise.all(
        items.map(
          item => this.updateItem(
            {
              id: item.id,
              active: isActive
            }
          ).catch(e => e)
        )
      )
    },
    onSort (event) {
      const {column, order} = event[0]
      column ? this.ordering = {key: column.dataKey, order} : this.ordering = null
      this.updateCurrentPage()
    },
    searchQueryChanged (search) {
      const previousSearchQuery = this.searchQuery
      this.searchQuery = search
      if (previousSearchQuery && !search) {
        // A convenient approach in fetching a new list when the search query is emptied.
        this.updateCurrentPage()
      }
    },
    showErrorMessage (action, errors) {
      const message = isEmpty(errors)
        ? `Failed to ${action} ${this.itemType}.`
        : `Failed to ${action} ${inflection.inflect(this.itemType, errors.length)}`
      this.$message.error(message)
    },
    showSuccessMessage (action, rows) {
      const message = isEmpty(rows)
        ? `${capitalize(this.itemType)} ${action}d successfully.`
        : `${capitalize(inflection.inflect(this.itemType, rows.length))} ${action}d successfully.`
      this.$message.success(message)
    },
    isNameError (error) {
      return [UniqueRecordError.errorName, CorruptedDataError.errorName].includes(error.errorName)
    },
    addErrorMessage (field, error) {
      this.$errors.add({
        field: field,
        msg: capitalize(error.message)
      })
    },
    deselectDataTableRows (selectedRows) {
      // We unselect rows which were not updated / has no state change to provide a consistent feel
      // on the overall UX where all rows will be unselected upon successful action performed
      selectedRows.map(row => { row.$selected = false })
    },
    toggleStatusLabel (selectedRows) {
      return selectedRows[0].active ? 'Mark as Inactive' : 'Mark as Active'
    },
    updateDataTableRows (items) {
      items.forEach(item => {
        const index = this.dataTableRows.findIndex(row => row.id === item.id)
        this.dataTableRows.splice(index, 1, item)
      })
    },
    async toggleStatus ({ selectedRows }) {
      this.toggleStatuses(!selectedRows[0].active, selectedRows)
    },
    async markAsActive ({ selectedRows }) {
      this.toggleStatuses(true, selectedRows)
    },
    async markAsInactive ({ selectedRows }) {
      this.toggleStatuses(false, selectedRows)
    },
    async toggleStatuses (isActive, selectedRows) {
      const action = this.determineActionTerm(isActive)
      const items = this.getActionableRows(selectedRows, isActive)

      this.isLoading = true
      const responses = await this.updateItems(items, isActive)
      this.isLoading = false

      // Error handling
      const errors = responses.filter(e => e instanceof Error)
      if (errors.length > 0) {
        this.showErrorMessage(action, errors)
        return
      }

      this.updateDataTableRows(responses.map(response => response.data))
      this.showSuccessMessage(action, selectedRows)
      this.deselectDataTableRows(selectedRows)
    },
    async updateCurrentPage (page = 1) {
      this.pagination.currentPage = page
      try {
        this.isLoading = true
        const {data, meta: {pagination}} = await this.fetchItems({
          pagination: this.pagination,
          search: this.searchQuery,
          sort: this.ordering
        })
        this.dataTableRows = data
        this.pagination = pagination
      } catch (e) {
        this.$message.error(`Failed to load ${capitalize(inflection.pluralize(this.itemType))}`)
        console.error(e.error)
      } finally {
        this.isLoading = false
      }
    },
    async submitDialogForm () {
      const isValid = await this.$validator.validateAll()
      if (!isValid) return
      this.isDialogOpen = false
      this.isLoading = true
      const action = 'create'

      try {
        await this.addItem(this.formData)
        // Upon success of adding new item, we will display first page of the data table.
        // As the sorting strategy is defaulted to "created on", the newly created item
        // would be displayed at the top of the data table in the first page.
        await this.updateCurrentPage()
      } catch (e) {
        if (this.isNameError(e)) {
          this.addErrorMessage('name', e)
          this.isDialogOpen = true
          return
        }
        this.showErrorMessage(action)
        return
      } finally {
        this.isLoading = false
      }
      this.showSuccessMessage(action)
      this.closeDialog()
    },
    openDialog () {
      this.isDialogOpen = true
    },
    closeDialog () {
      this.isDialogOpen = false
      this.formData = this.getDefaultFormValues()
      this.$errors.clear()
      this.$validator.reset()
    }
  },
  created () {
    if (this.itemType === 'note tag') {
      this.$validator.extend('noteTagFormat', {
        getMessage: () => 'The name should start with a letter from the English alphabet, consist 3-50 characters, and can contain dashes, underscores and/or whitespaces',
        validate: () => /^[a-zA-Z][\w\s-_]{2,49}$/.test(this.formData.name)
      })
    }

    this.updateCurrentPage()
  },
  render () {
    return this.$scopedSlots.default({
      // Base
      customPagination: this.customPagination,
      dataTableRows: this.dataTableRows,
      isLoading: this.isLoading,
      itemsPerPage: this.itemsPerPage,
      updateCurrentPage: this.updateCurrentPage,
      // Search
      searchQueryChanged: this.searchQueryChanged,
      // Sort
      onSort: this.onSort,
      // Add item modal
      closeDialog: this.closeDialog,
      formData: this.formData,
      isDialogOpen: this.isDialogOpen,
      openDialog: this.openDialog,
      submitDialogForm: this.submitDialogForm,
      // Activation and Deactivation
      markAsActive: this.markAsActive,
      markAsInactive: this.markAsInactive,
      toggleStatus: this.toggleStatus,
      toggleStatusLabel: this.toggleStatusLabel
    })
  }
}
